import React, { useState, useCallback, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SecurityIcon from '@mui/icons-material/Security';
import BadgeIcon from '@mui/icons-material/Badge';
import socialService, { ProfileAliasDto, CreateAliasRequestDto, UpdateAliasRequestDto } from '../../services/socialService';
import registrationService from '../../services/registrationService';
import accountService from '../../services/accountService';
import userAccountService from '../../services/userAccountService';
import { useAuth } from '../../hooks/useAuth';
import AccountClosureDialog from './AccountClosureDialog';
import CollapsibleSettingsSection from './CollapsibleSettingsSection';

interface AliasManagerProps {
  aliases: ProfileAliasDto[];
  onRefresh: () => void;
  /** When provided (e.g. when shown in a drawer), show a Close/Done button that calls this. */
  onClose?: () => void;
}

const ALIAS_USERNAME_REGEX = /^[a-zA-Z0-9_-]+$/;

// Validates that alias has at least 3 letters and 2 numbers
const validateLettersAndNumbers = (name: string): { valid: boolean; reason?: string } => {
  const letterCount = (name.match(/[a-zA-Z]/g) || []).length;
  const digitCount = (name.match(/[0-9]/g) || []).length;
  if (letterCount < 3 || digitCount < 2) {
    return { valid: false, reason: 'Must contain at least 3 letters and 2 numbers.' };
  }
  return { valid: true };
};

const AliasManager: React.FC<AliasManagerProps> = ({ aliases, onRefresh, onClose }) => {
  // Only count free aliases (types 1, 2, 3) toward the 3-alias limit
  // Type 4 = Credentialed (Business-Vendor, Business-Marketer, Business-Developer) - unlimited
  // Types 1-3 are personal aliases that count toward the free alias limit.
  // Type 4 is a credentialed business alias (unlimited).
  // Type 5 is a minor's account mirror (not a personal alias, does not count toward limit).
  const freeAliases = aliases.filter(a => a.intAliasType >= 1 && a.intAliasType <= 3);
  const freeAliasCount = freeAliases.length;
  const { user, loginHandle, logout: handleLogout } = useAuth();
  const navigate = useNavigate();
  const freeAliasesFull = freeAliasCount >= 3;
  const activeLoginHandle = (loginHandle || user?.username || '').trim().toLowerCase();
  const [accountEmail, setAccountEmail] = useState<string | undefined>();
  const [openDialog, setOpenDialog] = useState(false);
  const [moreNamesOpen, setMoreNamesOpen] = useState(false);
  const [newAlias, setNewAlias] = useState<CreateAliasRequestDto>({
    chrAliasName: '',
    chrDisplayName: '',
    intAliasType: 1
  });
  const [usernameAvailability, setUsernameAvailability] = useState<{ checking: boolean; available?: boolean; reason?: string }>({ checking: false });
  const [aliasNameTouched, setAliasNameTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editAlias, setEditAlias] = useState<ProfileAliasDto | null>(null);
  const [editForm, setEditForm] = useState<{ chrDisplayName: string; intAliasType: number }>({ chrDisplayName: '', intAliasType: 1 });
  const [editSaving, setEditSaving] = useState(false);

  // Account closure state
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);

  useEffect(() => {
    userAccountService.getAccountDetails()
      .then((details) => setAccountEmail(details.personalAccount?.email))
      .catch(() => undefined);
  }, []);

  const checkUsername = useCallback(async (name: string) => {
    const trimmed = (name || '').trim();
    if (trimmed.length < 3) {
      setUsernameAvailability({ checking: false, available: false, reason: 'At least 3 characters required.' });
      return;
    }
    if (!ALIAS_USERNAME_REGEX.test(trimmed)) {
      setUsernameAvailability({ checking: false, available: false, reason: 'Only letters, numbers, underscore (_), and hyphen (-) are allowed.' });
      return;
    }
    // Validate 3 letters + 2 numbers requirement
    const lettersNumbersCheck = validateLettersAndNumbers(trimmed);
    if (!lettersNumbersCheck.valid) {
      setUsernameAvailability({ checking: false, available: false, reason: lettersNumbersCheck.reason });
      return;
    }
    setUsernameAvailability({ checking: true });
    try {
      const result = await registrationService.checkUsername(trimmed, accountEmail);
      setUsernameAvailability({ checking: false, available: result.available, reason: result.reason });
    } catch {
      setUsernameAvailability({ checking: false, available: undefined, reason: 'Could not check availability.' });
    }
  }, [accountEmail]);

  const handleCreateAlias = async () => {
    const aliasName = (newAlias.chrAliasName || '').trim();
    if (aliasName.length < 3) {
      alert('Alias name must be at least 3 characters.');
      return;
    }
    if (!ALIAS_USERNAME_REGEX.test(aliasName)) {
      alert('Alias name can only contain letters, numbers, underscores (_), and hyphens (-).');
      return;
    }
    // Validate 3 letters + 2 numbers requirement
    const lettersNumbersCheck = validateLettersAndNumbers(aliasName);
    if (!lettersNumbersCheck.valid) {
      alert(lettersNumbersCheck.reason || 'Alias name must contain at least 3 letters and 2 numbers.');
      return;
    }
    if (freeAliasCount >= 3) {
      alert('You are allowed only three free aliases at this time. Credentialed business accounts are unlimited.');
      return;
    }
    setIsSubmitting(true);
    try {
      const avail = await registrationService.checkUsername(aliasName, accountEmail);
      if (!avail.available) {
        alert(avail.reason || 'This username is already taken.');
        return;
      }
      await socialService.createAlias(newAlias);
      setOpenDialog(false);
      setNewAlias({ chrAliasName: '', chrDisplayName: '', intAliasType: 1 });
      setUsernameAvailability({ checking: false });
      setAliasNameTouched(false);
      onRefresh();
    } catch (error: any) {
      alert(error.message || 'Failed to create alias');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetPrimary = async (aliasId: string) => {
    try {
      await socialService.setPrimaryAlias(aliasId);
      onRefresh();
    } catch (error) {
      alert('Failed to set primary alias');
    }
  };

  const handleToggleTrueNameLock = async (alias: ProfileAliasDto) => {
    const aliasId = (alias as any).idAliasID ?? (alias as any).IdAliasID;
    if (!aliasId) {
      alert('Invalid alias');
      return;
    }

    if (!alias.bitIdVerified) {
      alert('Verify your ID before an alias can be traceable or show your true name.');
      return;
    }

    const nextLockState = !Boolean(alias.bitTrueNameLocked);
    if (nextLockState) {
      const confirmed = confirm(
        'Show your true name on this alias?\n\n' +
        'Others will see the true name from Account Settings on this alias, the same way Address Book shows ID Verified Name. The alias stays unique.'
      );
      if (!confirmed) return;
    } else {
      const confirmed = confirm(
        'Stop showing your true name on this alias?\n\n' +
        'It will stay Traceable because your ID is verified, but the display name goes back to the alias name.'
      );
      if (!confirmed) return;
    }

    try {
      await socialService.setTrueNameLock(String(aliasId), nextLockState);
      onRefresh();
    } catch (error: any) {
      alert(error.message || 'Failed to update true-name lock');
    }
  };

  const handleDeleteAlias = async (alias: ProfileAliasDto) => {
    const aliasId = (alias as any).idAliasID ?? (alias as any).IdAliasID;

    // If this is the primary alias and there are others → user must reassign primary first
    if (alias.bitIsPrimary && aliases.length > 1) {
      alert(
        'The primary / locked alias cannot be deleted until another primary alias is selected (click the lock shut to select).\n\n' +
        'Alternatively, to close your entire account, remove all other aliases first so ' +
        'this is the only one remaining, then click its delete button.'
      );
      return;
    }

    // If this is the last remaining alias → offer full account closure
    if (alias.bitIsPrimary && aliases.length === 1) {
      // Fetch balance for the warning dialog
      try {
        const bal = await accountService.getBalance();
        setCurrentBalance(bal.availableBalance ?? 0);
      } catch {
        setCurrentBalance(0);
      }
      setClosureDialogOpen(true);
      return;
    }

    // Normal non-primary alias delete
    if (!confirm(`Are you sure you want to delete the alias "${alias.chrAliasName}"?`)) return;
    try {
      await socialService.deleteAlias(aliasId);
      onRefresh();
    } catch (error: any) {
      alert(error.message || 'Failed to delete alias');
    }
  };

  const handleOpenEdit = (alias: ProfileAliasDto) => {
    setEditAlias(alias);
    setEditForm({
      chrDisplayName: alias.chrDisplayName?.trim() || alias.chrAliasName || '',
      intAliasType: Number(alias.intAliasType) >= 1 && Number(alias.intAliasType) <= 5 ? alias.intAliasType : 2,
    });
  };

  const handleSaveEdit = async () => {
    if (!editAlias) return;
    const aliasId = (editAlias as any).idAliasID ?? (editAlias as any).IdAliasID;
    if (!aliasId) {
      alert('Invalid alias');
      return;
    }
    const nextDisplayName = editForm.chrDisplayName.trim() || editAlias.chrAliasName || '';
    const currentDisplayName = (editAlias.chrDisplayName || '').trim();
    const isChangingLockedVerifiedName =
      Boolean(editAlias.bitTrueNameLocked) &&
      Boolean(editAlias.bitIdVerified) &&
      nextDisplayName.length > 0 &&
      nextDisplayName !== currentDisplayName;
    if (isChangingLockedVerifiedName) {
      const confirmed = window.confirm(
        'Changing this display name will degrade verification status from "ID verified Name" to "Traceable". Continue?'
      );
      if (!confirmed) {
        return;
      }
    }
    setEditSaving(true);
    try {
      const request: UpdateAliasRequestDto = {
        chrDisplayName: nextDisplayName,
        intAliasType: editForm.intAliasType
      };
      await socialService.updateAlias(String(aliasId), request);
      setEditAlias(null);
      onRefresh();
    } catch (error: any) {
      alert(error.message || 'Failed to update alias');
    } finally {
      setEditSaving(false);
    }
  };

  const getAliasTypeColor = (type: number) => {
    switch (type) {
      case 1: return 'primary';
      case 2: return 'secondary';
      case 3: return 'default';
      case 5: return 'info';   // Minor's Account
      default: return 'default';
    }
  };

  const getDisplayAliasType = (alias: ProfileAliasDto): { label: string; type: number } => {
    const normalizedType = Number(alias.intAliasType) >= 1 && Number(alias.intAliasType) <= 5
      ? Number(alias.intAliasType)
      : 2;
    const rawLabel = (alias.aliasTypeName || '').trim();
    const label = !rawLabel || rawLabel.toLowerCase() === 'unknown' ? 'Private' : rawLabel;
    return { label, type: normalizedType };
  };

  const [explainerOpen, setExplainerOpen] = useState(() => {
    try {
      return localStorage.getItem('mailbox.aliasExplainerOpen') !== '0';
    } catch {
      return true;
    }
  });

  const setExplainerOpenPersisted = (open: boolean) => {
    setExplainerOpen(open);
    try {
      localStorage.setItem('mailbox.aliasExplainerOpen', open ? '1' : '0');
    } catch {
      /* private mode */
    }
  };

  const trimmedAliasName = (newAlias.chrAliasName || '').trim();
  const aliasNameValid = trimmedAliasName.length >= 3 && 
    ALIAS_USERNAME_REGEX.test(trimmedAliasName) && 
    validateLettersAndNumbers(trimmedAliasName).valid;
  const canCreate = aliasNameValid && freeAliasCount < 3;

  return (
    <Card>
      <CardContent>
        <CollapsibleSettingsSection
          sectionId="alias-guide"
          expanded={explainerOpen}
          onToggle={() => setExplainerOpenPersisted(!explainerOpen)}
          sx={{ mb: 1 }}
          title={(
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#56903a' }}>
              How aliases, display names, and your balance fit together
            </Typography>
          )}
        >
        <Box
          sx={{
            p: 2.5,
            mb: 2,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            color: '#000',
            backgroundImage: (theme) =>
              `linear-gradient(180deg, ${theme.palette.action.hover} 0%, transparent 72px)`,
            '& .MuiTypography-subtitle1, & .MuiTypography-subtitle2': {
              color: '#56903a',
            },
            '& .MuiLink-root': {
              color: '#56903a',
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0 }}>
            An address for mail and for credit history
          </Typography>
          <Typography variant="body2"  paragraph>
            Each alias is a messaging address. People send mail to that name, and exchanges of Qix Credits
            in your account history are attributed to the alias you used for that transaction.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            One shared balance
          </Typography>
          <Typography variant="body2"  paragraph>
            All of your aliases share the same Qix Credits balance. Money received or spent under any alias
            changes that one balance. One password signs you in, whichever alias you type.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            A profile of its own
          </Typography>
          <Typography variant="body2"  paragraph>
            Each alias has a separate profile, similar to a social profile: photo, bio, and links.
            You choose how much of that profile other people can see. Hiding a profile does not hide
            the alias as a mail address.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            The primary alias
          </Typography>
          <Typography variant="body2"  paragraph>
            One alias is primary. It is the account owner. Click an open lock to shut it and select that
            alias as primary. The primary / locked alias cannot be deleted until another primary alias is
            selected. If it is your only alias, deleting it closes the account. Click the shield to choose
            which aliases also show your true name.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Display names are not aliases
          </Typography>
          <Typography variant="body2"  paragraph>
            The alias is the unique address, such as BlueJelly99. It is not case-sensitive, so John
            and joHn are the same name, and it needs at least 3 letters and 2 numbers. The display name
            is the label people see, such as Blue Jelly. Display names do not have to be unique, so more
            than one person can show the same label. The alias is what mail and credit history use.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Verified and Traceable Identities
          </Typography>
          <Typography variant="body2"  paragraph>
            By default, aliases are anonymous. Your display name may, or may not be, your legal name.
          </Typography>
          <Typography variant="body2"  paragraph>
            If you want to verify for others that your display name is accurate, you can complete the
            verification of a government ID in Account settings. Once this is done, &apos;ID Verified Name&apos;
            badge will appear next to any alias and any matching user name you choose. This badge gives
            others confidence they are communicating with the real you.
          </Typography>
          <Typography variant="body2"  paragraph>
            Once a user is ID verified, any anonymous aliases associated with that account will also
            receive the Traceable badge. In the event of a court order regarding criminal conduct
            involving an anonymous but traceable alias, the ID verified name and government ID number
            can be shared with authorities. This discourages scammers and bad actors from using even an
            anonymous alias, and gives you greater confidence when dealing with both ID Verified and
            Traceable aliases.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            How many you can create?
          </Typography>
          <Typography variant="body2" paragraph>
            Personal accounts include up to three aliases at no charge. A useful pair is a public name
            such as John_Smith-1040 and a less identifying name such as BlueJelly99. A semi-anonymous
            alias does not show your true name to vendors or advertisers, and it still belongs to this
            same account and balance.
          </Typography>
          <Typography variant="body2">
            After those three, add another name in one of two ways. A premium username is a paid name,
            and it can be letters only. Open{' '}
            <Link component={RouterLink} to="/profile?tab=account-settings">Account settings</Link>
            {' '}and use Premium Usernames. A credentialed business account is not limited to three
            aliases. Start in the{' '}
            <Link component={RouterLink} to="/credentialing">Credentialing Wizard</Link>
            {' '}and follow the steps to credential the account.
          </Typography>
        </Box>
        </CollapsibleSettingsSection>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5">
            Create and Manage Aliases
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onClose && (
              <Button variant="outlined" onClick={onClose}>
                Done
              </Button>
            )}
            {freeAliasesFull ? (
              <Button variant="contained" onClick={() => setMoreNamesOpen(true)}>
                Get another name
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setOpenDialog(true);
                  setUsernameAvailability({ checking: false });
                  setAliasNameTouched(false);
                }}
              >
                Create Alias
              </Button>
            )}
          </Box>
        </Box>

        <List>
          {aliases.map((alias) => {
            const displayAliasType = getDisplayAliasType(alias);
            // Login alias = the handle typed at sign-in (may differ from base account username).
            const isLoginAlias = !!activeLoginHandle &&
              (alias.chrAliasName || '').trim().toLowerCase() === activeLoginHandle;
            const displayName = (alias.chrDisplayName || alias.chrAliasName || '').trim();
            const handle = (alias.chrAliasName || '').trim();
            const isOnlyAlias = aliases.length === 1;

            return (
              <React.Fragment key={alias.idAliasID}>
                {alias.bitIsPrimary && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Alert
                      severity={isOnlyAlias ? 'warning' : 'info'}
                      icon={<InfoOutlinedIcon />}
                      sx={{ fontSize: '0.82rem' }}
                    >
                      {isOnlyAlias ? (
                        <>
                          <strong>This is your only alias and the account owner.</strong> Deleting it will
                          permanently close your entire Summa account, abandon any remaining balance, and
                          release this username for others to register.
                        </>
                      ) : (
                        <>
                          <strong>Primary alias — account owner.</strong> The primary / locked alias cannot
                          be deleted until another primary alias is selected (click the lock shut to select).
                        </>
                      )}
                    </Alert>
                  </Box>
                )}
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {displayName}
                        <Chip
                          label={displayAliasType.label}
                          size="small"
                          color={getAliasTypeColor(displayAliasType.type) as any}
                        />
                        {alias.bitIdVerified && alias.bitTrueNameLocked && (
                          <Chip
                            label="ID Verified Name"
                            size="small"
                            color="success"
                            variant="outlined"
                            icon={<BadgeIcon />}
                          />
                        )}
                        {alias.bitIdVerified && !alias.bitTrueNameLocked && (
                          <Chip label="Traceable" size="small" color="success" variant="outlined" />
                        )}
                        {!alias.bitIdVerified && (
                          <Chip label="Untraceable" size="small" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={`@${handle}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleOpenEdit(alias)} title="Edit display name and profile status">
                      <EditIcon />
                    </IconButton>
                    {alias.bitIsPrimary ? (
                      <Box component="span" title="This alias is the primary / locked alias">
                        <IconButton disabled color="primary" aria-label="Primary alias">
                          <LockIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <IconButton
                        onClick={() => handleSetPrimary(alias.idAliasID)}
                        title="Click the lock shut to select this alias as primary"
                      >
                        <LockOpenIcon />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={() => handleToggleTrueNameLock(alias)}
                      color={
                        !alias.bitIdVerified
                          ? 'default'
                          : alias.bitTrueNameLocked
                            ? 'info'
                            : 'primary'
                      }
                      title={
                        !alias.bitIdVerified
                          ? 'Untraceable: ID is not verified'
                          : alias.bitTrueNameLocked
                            ? 'ID Verified Name: this alias shows your true name. Click the shield to stop showing it.'
                            : 'Traceable: ID is verified and the true name is hidden. Click the shield to show your true name on this alias.'
                      }
                      sx={{ opacity: alias.bitIdVerified ? 1 : 0.4 }}
                    >
                      <Box sx={{ position: 'relative', display: 'inline-flex', lineHeight: 0 }}>
                        <SecurityIcon />
                        {!alias.bitIdVerified && (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: -1,
                              right: -1,
                              top: '46%',
                              height: '2px',
                              bgcolor: 'text.disabled',
                              transform: 'rotate(-28deg)',
                            }}
                          />
                        )}
                      </Box>
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteAlias(alias)}
                      title={alias.bitIsPrimary && aliases.length > 1
                        ? 'Primary alias cannot be deleted until another is selected'
                        : alias.bitIsPrimary && isOnlyAlias
                          ? 'Close account (last alias)'
                          : 'Delete alias'}
                      color={alias.bitIsPrimary && isOnlyAlias ? 'error' : 'default'}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>

        {/* Create Alias Dialog */}
        <Dialog open={moreNamesOpen} onClose={() => setMoreNamesOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Your three free aliases are in use</DialogTitle>
          <DialogContent>
            <Typography variant="body2" paragraph>
              A premium username is a paid name. It can be letters only, without the numbers a free
              alias needs. Open Account settings and use Premium Usernames.
            </Typography>
            <Typography variant="body2">
              A credentialed business account is not limited to three aliases. The Credentialing Wizard
              walks through credentialing that account.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
            <Button onClick={() => setMoreNamesOpen(false)}>Close</Button>
            <Button
              variant="outlined"
              onClick={() => {
                setMoreNamesOpen(false);
                navigate('/profile?tab=account-settings');
              }}
            >
              Account settings
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setMoreNamesOpen(false);
                navigate('/credentialing');
              }}
            >
              Credentialing Wizard
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Create New Alias</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Alias Name"
                value={newAlias.chrAliasName}
                onChange={(e) => {
                  const aliasName = e.target.value;
                  setNewAlias((prev) => ({
                    ...prev,
                    chrAliasName: aliasName,
                    chrDisplayName: prev.chrDisplayName.trim() ? prev.chrDisplayName : aliasName,
                  }));
                  setUsernameAvailability({ checking: false });
                }}
                onBlur={() => {
                  setAliasNameTouched(true);
                  checkUsername(newAlias.chrAliasName);
                }}
                error={aliasNameTouched && usernameAvailability.available === false}
                helperText={
                  aliasNameTouched && usernameAvailability.checking
                    ? 'Checking...'
                    : aliasNameTouched && usernameAvailability.available === false
                      ? usernameAvailability.reason
                      : 'Must have at least 3 letters and 2 numbers. Allowed: letters, numbers, underscore (_), hyphen (-). Not case-sensitive.'
                }
                placeholder="e.g. John_Smith-1040 or BlueJelly99"
              />
              <TextField
                label="Display Name"
                value={newAlias.chrDisplayName}
                onChange={(e) => setNewAlias({ ...newAlias, chrDisplayName: e.target.value })}
                helperText="Shown as the 'From' name in emails and on your profile. Defaults to the alias name above if left blank."
                placeholder="Defaults to alias name"
              />
              <FormControl fullWidth>
                <InputLabel>Alias Type</InputLabel>
                <Select
                  value={newAlias.intAliasType}
                  onChange={(e) => setNewAlias({ ...newAlias, intAliasType: Number(e.target.value) })}
                  label="Alias Type"
                >
                  <MenuItem value={1}>Public</MenuItem>
                  <MenuItem value={2}>Private</MenuItem>
                  <MenuItem value={3}>Semi-Anonymous</MenuItem>
                </Select>
                <FormHelperText>
                  {newAlias.intAliasType === 1 && 'Profile can be public; your name can be linked to this username.'}
                  {newAlias.intAliasType === 2 && 'No public profile; only you see this alias. Good for minimal footprint.'}
                  {newAlias.intAliasType === 3 && 'Profile can be public (e.g. for gaming or interests) but your real identity is not shared with vendors; traceable to your account for fraud/legal.'}
                </FormHelperText>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAlias} variant="contained" disabled={!canCreate || isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Alias Dialog - change display name and profile status (public/private/semi-anonymous) */}
        <Dialog open={!!editAlias} onClose={() => setEditAlias(null)}>
          <DialogTitle>Edit alias</DialogTitle>
          <DialogContent>
            {editAlias && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, minWidth: 320 }}>
                <Typography variant="body2" color="text.secondary">
                  @{editAlias.chrAliasName} (username cannot be changed)
                </Typography>
                <TextField
                  label="Display Name"
                  value={editForm.chrDisplayName}
                  onChange={(e) => setEditForm({ ...editForm, chrDisplayName: e.target.value })}
                  helperText={editAlias.bitTrueNameLocked
                    ? 'This alias is currently ID Verified Name locked. Changing it will downgrade status to Traceable.'
                    : 'Shown in emails and on your profile'}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Profile status</InputLabel>
                  <Select
                    value={editForm.intAliasType}
                    onChange={(e) => setEditForm({ ...editForm, intAliasType: Number(e.target.value) })}
                    label="Profile status"
                  >
                    <MenuItem value={1}>Public</MenuItem>
                    <MenuItem value={2}>Private</MenuItem>
                    <MenuItem value={3}>Semi-Anonymous</MenuItem>
                  </Select>
                  <FormHelperText>
                    {editForm.intAliasType === 1 && 'Profile can be public; your name can be linked to this username.'}
                    {editForm.intAliasType === 2 && 'No public profile; only you see this alias.'}
                    {editForm.intAliasType === 3 && 'Profile can be public but your real identity is not shared with vendors.'}
                  </FormHelperText>
                </FormControl>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditAlias(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} variant="contained" disabled={editSaving}>
              {editSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>

      {/* Account Closure Dialog — triggered when user tries to delete their only/primary alias */}
      <AccountClosureDialog
        open={closureDialogOpen}
        onClose={() => setClosureDialogOpen(false)}
        onClosureComplete={async () => {
          setClosureDialogOpen(false);
          await handleLogout();
        }}
        currentBalance={currentBalance}
      />
    </Card>
  );
};

export default AliasManager;

